import React, { useMemo, useState } from "react";
import LoadingState from "./LoadingState";

const PAGE_SIZE = 10;

const formatRole = (role) => {
  const map = {
    platform_admin: "Platform Admin",
    tenant_admin: "Tenant Admin",
    support_agent: "Support Agent",
    viewer: "Viewer",
    system: "System",
  };
  return map[role] || role || "System";
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatValue = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") {
    const s = JSON.stringify(val);
    return s.length > 60 ? s.slice(0, 57) + "…" : s;
  }
  return String(val);
};

const TenantAuditTab = ({ auditLogs, loading, error }) => {
  const [search, setSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState("all");
  const [page, setPage] = useState(1);

  const entities = useMemo(() => {
    const set = new Set((auditLogs || []).map((l) => l.entity_type).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [auditLogs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (auditLogs || []).filter((log) => {
      const matchesEntity = entityFilter === "all" || log.entity_type === entityFilter;
      const matchesSearch =
        !q ||
        [log.action, log.user_id, log.entity_type, log.entity_id, log.ip_address]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      return matchesEntity && matchesSearch;
    });
  }, [auditLogs, search, entityFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleEntityChange = (e) => { setEntityFilter(e.target.value); setPage(1); };

  if (loading) return <LoadingState label="Loading audit logs..." />;

  if (error) {
    return (
      <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-6 py-12 text-center">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-rose-800">Failed to load audit logs</p>
          <p className="text-xs text-rose-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[1fr_200px]">
        <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
          <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="m21 21-4.35-4.35m1.85-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search action, actor, or entity…"
            className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
        </label>
        <label className="flex flex-col justify-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Entity</span>
          <select
            value={entityFilter}
            onChange={handleEntityChange}
            className="bg-transparent text-sm font-medium text-slate-900 outline-none"
          >
            {entities.map((e) => (
              <option key={e} value={e}>{e === "all" ? "All entities" : e}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Time</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Action</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Entity</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Actor</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Old → New</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400">
                    {search || entityFilter !== "all"
                      ? "No audit logs match your search or filter."
                      : "No audit logs yet. Data will appear once the backend audit endpoint is connected for this tenant."}
                  </td>
                </tr>
              ) : paginated.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-4 text-xs text-slate-500 whitespace-nowrap">
                    {formatDateTime(log.created_at)}
                  </td>
                  <td className="px-5 py-4 font-semibold text-slate-950 whitespace-nowrap">
                    {log.action || "—"}
                  </td>
                  <td className="px-5 py-4 text-slate-600">
                    {log.entity_type || "—"}
                  </td>
                  <td className="px-5 py-4 text-slate-700 whitespace-nowrap">
                    {log.user_id && isNaN(log.user_id) ? log.user_id : formatRole(log.actor_role)}
                  </td>
                  <td className="px-5 py-4 max-w-[240px]">
                    <span className="block truncate text-xs text-slate-500" title={`${formatValue(log.old_value_json)} → ${formatValue(log.new_value_json)}`}>
                      {formatValue(log.old_value_json)} → {formatValue(log.new_value_json)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
          <p className="text-xs text-slate-500">
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            {filtered.length !== (auditLogs || []).length ? ` (filtered from ${auditLogs.length})` : ""}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs font-medium text-slate-600">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantAuditTab;
